import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, VesselClass, VesselRegistration, VesselSurvey } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TrafficLight } from '../../ui/components';

const ANY_OFFICER = ['SHIP_REGISTRAR', 'MARINE_SURVEYOR', 'PORT_OFFICER', 'SEAFARER_REGISTRAR'];
const REGISTRAR_ROLES = ['SHIP_REGISTRAR'];
const VESSEL_CLASSES: VesselClass[] = ['LOCAL_SMALL_CRAFT', 'FISHING', 'COMMERCIAL', 'INTERNATIONAL_FLAG'];
const STATES = [
  'APPLICATION',
  'DOC_REVIEW',
  'SURVEY',
  'EXEMPTION_ROUTE',
  'REGISTRY_APPROVAL',
  'FEE_PENDING',
  'PROVISIONAL_CERT',
  'FULL_REGISTRATION',
  'REJECTED',
  'WITHDRAWN',
  'DEREGISTERED',
];

/**
 * M22 — vessel registry: APPLICATION -> DOC_REVIEW -> SURVEY|EXEMPTION_ROUTE
 * -> REGISTRY_APPROVAL -> FEE_PENDING -> [PROVISIONAL_CERT ->] FULL_REGISTRATION.
 * Surveys have no standalone list endpoint — they're embedded in the vessel
 * detail response (GET /vessels/:id includes `surveys`) and managed inline
 * here, same reasoning as the age-exemption workflow's linked survey.
 */
export function Vessels() {
  const { persona } = useAuth();
  const isOfficer = persona ? persona.roles.some((r) => ANY_OFFICER.includes(r)) : false;
  const isRegistrar = persona ? persona.roles.some((r) => REGISTRAR_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [vesselClass, setVesselClass] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showRegister, setShowRegister] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (vesselClass) params.set('vesselClass', vesselClass);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m22/vessels?${params.toString()}`;
  }, [status, vesselClass, appliedQ]);

  const listQ = useApiQuery<Page<VesselRegistration>>(path);

  return (
    <div>
      <PageHeader
        title="Vessel Registry"
        subtitle="Local + international flag vessel registration, seaworthiness surveys, and certificate lifecycle (M22 — SMSA)."
        actions={
          <>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedQ(q);
              }}
            >
              <input className="field-input !w-40" placeholder="Reg no. or name…" value={q} onChange={(e) => setQ(e.target.value)} />
              <select className="field-input !w-auto" value={vesselClass} onChange={(e) => setVesselClass(e.target.value)}>
                <option value="">All classes</option>
                {VESSEL_CLASSES.map((c) => (
                  <option key={c} value={c}>
                    {c.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-secondary">
                Search
              </button>
            </form>
            {isOfficer ? (
              <button className="btn-primary" onClick={() => setShowRegister(true)}>
                + Register vessel
              </button>
            ) : null}
          </>
        }
      />

      {!isOfficer ? <RoleNotice roles={ANY_OFFICER} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No vessels match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Reg. no.</th>
                <th className="table-head">Name</th>
                <th className="table-head">Class</th>
                <th className="table-head">Owner</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((v) => (
                <tr key={v.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(v.id)}>
                  <td className="table-cell font-mono text-xs font-semibold">{v.regNo}</td>
                  <td className="table-cell">{v.name}</td>
                  <td className="table-cell text-xs text-slate-500">{v.vesselClass.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">
                    {v.ownerType} · {v.ownerId ?? '—'}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={v.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showRegister ? (
        <RegisterVesselModal
          onClose={() => setShowRegister(false)}
          onCreated={() => {
            setShowRegister(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selectedId ? (
        <VesselDetailModal
          id={selectedId}
          isRegistrar={isRegistrar}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function RegisterVesselModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [vesselClass, setVesselClass] = useState<VesselClass>('LOCAL_SMALL_CRAFT');
  const [name, setName] = useState('');
  const [imoNumber, setImoNumber] = useState('');
  const [loaMetres, setLoaMetres] = useState('');
  const [grossTonnage, setGrossTonnage] = useState('');
  const [yearBuilt, setYearBuilt] = useState(String(new Date().getUTCFullYear()));
  const [ownerType, setOwnerType] = useState<'PERSON' | 'ENTITY'>('PERSON');
  const [ownerId, setOwnerId] = useState('');
  const [portOfRegistry, setPortOfRegistry] = useState('Port Victoria');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m22/vessels', {
        vesselClass,
        name: name.trim(),
        ...(imoNumber.trim() ? { imoNumber: imoNumber.trim() } : {}),
        loaMetres: Number.parseFloat(loaMetres),
        grossTonnage: Number.parseFloat(grossTonnage),
        yearBuilt: Number.parseInt(yearBuilt, 10),
        ownerType,
        ownerId: ownerId.trim(),
        portOfRegistry: portOfRegistry.trim() || undefined,
        ...(vesselClass === 'INTERNATIONAL_FLAG' ? { foreignOwnerStructure: { note: 'Recorded at counter — see application file' } } : {}),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register vessel');
    } finally {
      setBusy(false);
    }
  };

  const valid = name.trim().length >= 2 && loaMetres && grossTonnage && ownerId.trim().length >= 1;

  return (
    <Modal title="Register a vessel" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Vessel class">
          <select className="field-input" value={vesselClass} onChange={(e) => setVesselClass(e.target.value as VesselClass)}>
            {VESSEL_CLASSES.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Vessel name" className="col-span-2">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="IMO number (optional)">
          <input className="field-input" value={imoNumber} onChange={(e) => setImoNumber(e.target.value)} />
        </Field>
        <Field label="Length overall (m)">
          <input className="field-input" type="number" value={loaMetres} onChange={(e) => setLoaMetres(e.target.value)} />
        </Field>
        <Field label="Gross tonnage">
          <input className="field-input" type="number" value={grossTonnage} onChange={(e) => setGrossTonnage(e.target.value)} />
        </Field>
        <Field label="Year built">
          <input className="field-input" type="number" value={yearBuilt} onChange={(e) => setYearBuilt(e.target.value)} />
        </Field>
        <Field label="Owner type">
          <select className="field-input" value={ownerType} onChange={(e) => setOwnerType(e.target.value as 'PERSON' | 'ENTITY')}>
            <option value="PERSON">Person (NIN)</option>
            <option value="ENTITY">Entity (business id)</option>
          </select>
        </Field>
        <Field label={ownerType === 'PERSON' ? 'Owner NIN' : 'Owner business id'}>
          <input className="field-input" value={ownerId} onChange={(e) => setOwnerId(e.target.value)} />
        </Field>
        <Field label="Port of registry">
          <input className="field-input" value={portOfRegistry} onChange={(e) => setPortOfRegistry(e.target.value)} />
        </Field>
      </div>
      {vesselClass === 'INTERNATIONAL_FLAG' ? (
        <p className="mt-2 text-xs text-amber-600">International flag registrations require a foreign owner structure record — a placeholder note is submitted; capture the real structure in the case file.</p>
      ) : null}
      <button className="btn-primary mt-4" disabled={busy || !valid} onClick={submit}>
        {busy ? 'Registering…' : 'Submit registration'}
      </button>
    </Modal>
  );
}

function VesselDetailModal({
  id,
  isRegistrar,
  onClose,
  onChanged,
}: {
  id: string;
  isRegistrar: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const q = useApiQuery<VesselRegistration>(`/api/v1/m22/vessels/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approve, setApprove] = useState<boolean | null>(null);
  const [note, setNote] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showDeregister, setShowDeregister] = useState(false);
  const [reason, setReason] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const v = q.data;

  return (
    <Modal title={v ? `${v.regNo} — ${v.name}` : 'Vessel registration'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {v ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={v.status} />} />
            <Detail label="Class" value={v.vesselClass.replaceAll('_', ' ')} />
            <Detail label="Cert expiry" value={<TrafficLight status={v.certExpiryStatus ?? 'NONE'} />} />
            <Detail label="LOA" value={`${v.loaMetres} m`} />
            <Detail label="Gross tonnage" value={String(v.grossTonnage)} />
            <Detail label="Year built" value={String(v.yearBuilt)} />
            <Detail label="Owner" value={`${v.ownerType} · ${v.ownerId ?? '—'}`} />
            <Detail label="Port of registry" value={v.portOfRegistry} />
            {v.imoNumber ? <Detail label="IMO number" value={v.imoNumber} mono /> : null}
          </div>

          {v.rejectionReason ? <ErrorNote message={`Rejected: ${v.rejectionReason}`} /> : null}
          {v.docReviewNote ? <Detail label="Doc review note" value={`${v.docReviewNote} (${v.docReviewBy})`} /> : null}

          {v.certificates && v.certificates.length > 0 ? (
            <div>
              <div className="field-label">Certificates</div>
              <div className="space-y-1.5 text-sm">
                {v.certificates.map((c) => (
                  <div key={c.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-1.5">
                    <span>
                      {c.certType.replaceAll('_', ' ')} {c.expiresAt ? `· expires ${formatDate(c.expiresAt)}` : ''}
                    </span>
                    <StatusBadge status={c.status} />
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {v.surveys && v.surveys.length > 0 ? (
            <div>
              <div className="field-label">Surveys</div>
              <div className="space-y-2">
                {v.surveys.map((s) => (
                  <SurveyRow key={s.id} survey={s} isRegistrar={isRegistrar} onChanged={() => q.reload()} />
                ))}
              </div>
            </div>
          ) : null}

          {v.ageExemptions && v.ageExemptions.length > 0 ? (
            <div className="text-xs text-slate-500">
              This vessel has {v.ageExemptions.length} age-exemption application(s) — manage them from the Age Exemptions workspace.
            </div>
          ) : null}

          {isRegistrar ? (
            <div className="space-y-3 rounded-lg border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700">Registrar actions</div>

              {v.status === 'APPLICATION' ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <button className={`btn-success ${approve === true ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setApprove(true)}>
                      Approve documents
                    </button>
                    <button className={`btn-danger ${approve === false ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setApprove(false)}>
                      Reject documents
                    </button>
                  </div>
                  {approve !== null ? (
                    <>
                      <Field label="Note (optional)">
                        <input className="field-input" value={note} onChange={(e) => setNote(e.target.value)} />
                      </Field>
                      {!approve ? (
                        <Field label="Rejection reason">
                          <textarea className="field-input" rows={2} value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} />
                        </Field>
                      ) : null}
                      <button
                        className={approve ? 'btn-success' : 'btn-danger'}
                        disabled={busy}
                        onClick={() =>
                          call(() =>
                            api.post(`/api/v1/m22/vessels/${v.id}/doc-review`, {
                              approve,
                              ...(note.trim() ? { note: note.trim() } : {}),
                              ...(!approve ? { rejectionReason: rejectionReason.trim() } : {}),
                            }),
                          )
                        }
                      >
                        {busy ? 'Submitting…' : `Confirm ${approve ? 'approval' : 'rejection'}`}
                      </button>
                    </>
                  ) : null}
                </div>
              ) : null}

              {v.status === 'REGISTRY_APPROVAL' ? (
                <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m22/vessels/${v.id}/approve-registry`))}>
                  {busy ? 'Approving…' : 'Give final registry approval (creates fee intent)'}
                </button>
              ) : null}

              {v.status === 'PROVISIONAL_CERT' ? (
                <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m22/vessels/${v.id}/finalize-registration`))}>
                  {busy ? 'Finalizing…' : 'Finalize full registration'}
                </button>
              ) : null}

              {v.status === 'FULL_REGISTRATION' ? (
                !showDeregister ? (
                  <button className="btn-danger" onClick={() => setShowDeregister(true)}>
                    Deregister vessel
                  </button>
                ) : (
                  <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                    <Field label="Deregistration reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button
                      className="btn-danger"
                      disabled={busy || reason.trim().length < 3}
                      onClick={() => call(() => api.post(`/api/v1/m22/vessels/${v.id}/deregister`, { reason: reason.trim() }))}
                    >
                      {busy ? 'Deregistering…' : 'Confirm deregister (revokes all active certificates)'}
                    </button>
                  </div>
                )
              ) : null}

              {!['FULL_REGISTRATION', 'REJECTED', 'WITHDRAWN', 'DEREGISTERED'].includes(v.status) ? (
                !showWithdraw ? (
                  <button className="btn-secondary" onClick={() => setShowWithdraw(true)}>
                    Withdraw application
                  </button>
                ) : (
                  <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                    <Field label="Withdrawal reason (optional)">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button
                      className="btn-secondary"
                      disabled={busy}
                      onClick={() => call(() => api.post(`/api/v1/m22/vessels/${v.id}/withdraw`, reason.trim() ? { reason: reason.trim() } : {}))}
                    >
                      {busy ? 'Withdrawing…' : 'Confirm withdraw'}
                    </button>
                  </div>
                )
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={REGISTRAR_ROLES} />
          )}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Applied {formatDateTime(v.createdAt)} · last updated {formatDateTime(v.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function SurveyRow({ survey, isRegistrar, onChanged }: { survey: VesselSurvey; isRegistrar: boolean; onChanged: () => void }) {
  const { persona } = useAuth();
  const isAssignedSurveyor = persona?.roles.includes('MARINE_SURVEYOR') ?? false;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAppoint, setShowAppoint] = useState(false);
  const [surveyorId, setSurveyorId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [recommendation, setRecommendation] = useState<'APPROVE' | 'REJECT'>('APPROVE');
  const [findings, setFindings] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-100 p-3 text-sm">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="font-medium">{survey.surveyType.replaceAll('_', ' ')} survey</span>
        <StatusBadge status={survey.status} />
      </div>
      {survey.surveyorId ? <div className="text-xs text-slate-500">Surveyor: {survey.surveyorId}</div> : null}
      {survey.findings ? <div className="mt-1 text-xs text-slate-500">Findings: {survey.findings}</div> : null}
      {error ? (
        <div className="mt-2">
          <ErrorNote message={error} />
        </div>
      ) : null}

      {isRegistrar && survey.status === 'PENDING_APPOINTMENT' ? (
        !showAppoint ? (
          <button className="btn-secondary mt-2 !py-1 !text-xs" onClick={() => setShowAppoint(true)}>
            Appoint surveyor
          </button>
        ) : (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Field label="Surveyor id">
              <input className="field-input" value={surveyorId} onChange={(e) => setSurveyorId(e.target.value)} />
            </Field>
            <Field label="Scheduled at">
              <input className="field-input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
            </Field>
            <button
              className="btn-primary col-span-2"
              disabled={busy || !surveyorId.trim() || !scheduledAt}
              onClick={() =>
                call(() =>
                  api.post(`/api/v1/m22/surveys/${survey.id}/appoint`, {
                    surveyorId: surveyorId.trim(),
                    scheduledAt: new Date(scheduledAt).toISOString(),
                  }),
                )
              }
            >
              {busy ? 'Appointing…' : 'Confirm appointment'}
            </button>
          </div>
        )
      ) : null}

      {(isRegistrar || isAssignedSurveyor) && survey.status === 'APPOINTED' ? (
        !showReport ? (
          <button className="btn-secondary mt-2 !py-1 !text-xs" onClick={() => setShowReport(true)}>
            Submit report
          </button>
        ) : (
          <div className="mt-2 space-y-2">
            <div className="flex gap-2">
              <button className={`btn-success !py-1 !text-xs ${recommendation === 'APPROVE' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setRecommendation('APPROVE')}>
                Recommend approve
              </button>
              <button className={`btn-danger !py-1 !text-xs ${recommendation === 'REJECT' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setRecommendation('REJECT')}>
                Recommend reject
              </button>
            </div>
            <Field label="Findings">
              <textarea className="field-input" rows={2} value={findings} onChange={(e) => setFindings(e.target.value)} />
            </Field>
            <button
              className={recommendation === 'APPROVE' ? 'btn-success' : 'btn-danger'}
              disabled={busy || findings.trim().length < 3}
              onClick={() => call(() => api.post(`/api/v1/m22/surveys/${survey.id}/report`, { recommendation, findings: findings.trim() }))}
            >
              {busy ? 'Submitting…' : 'Submit report'}
            </button>
          </div>
        )
      ) : null}
    </div>
  );
}
